import Svg, { Path } from "react-native-svg";

import { type BrandName, brandPaths } from "@/lib/shop/brandLink";

/**
 * One brand mark for a storefront link (TRO-1171).
 *
 * A sibling of `ui/Icon` rather than a call site of it: `Icon` requires an SF Symbol name and a
 * lucide component at every use, and neither set ships brand logos. These are drawn from path data
 * instead, identically on every platform, which is also what a logo wants — a brand mark is not an
 * ambient UI glyph and should not change vocabulary between iOS and Android.
 *
 * Filled marks take `color` as their fill; the two fallbacks (a link, an envelope) are lucide
 * outlines and take it as a stroke. That difference is deliberate and visible: a logo reads as a
 * logo, and "some website" reads as an icon.
 */
export function BrandGlyph(
  { name, size = 18, color }: {
    /** Which brand to draw, including the "website" and "email" fallbacks. */
    name: BrandName;
    size?: number;
    /** Fill for a brand logo, stroke for the two fallback outlines. */
    color: string;
  },
) {
  const paths = brandPaths(name);
  return (
    <Svg width={size} height={size} viewBox="0 0 24 24" accessibilityRole="image" aria-hidden>
      {"filled" in paths
        ? <Path d={paths.filled} fill={color} />
        : paths.stroked.map((d) => (
          <Path
            key={d}
            d={d}
            stroke={color}
            strokeWidth={2}
            strokeLinecap="round"
            strokeLinejoin="round"
            fill="none"
          />
        ))}
    </Svg>
  );
}
